import { useId, useState, type KeyboardEvent } from 'react';
import { motion } from 'motion/react';
import { cn, clamp } from './util';

/** Clamps `value` into [min, max] and snaps it to the `step` grid anchored at `min`. */
export function clampStep(value: number, min: number, max: number, step = 1): number {
  if (!Number.isFinite(value)) return min;
  const snapped = min + Math.round((value - min) / step) * step;
  // Avoid float noise for fractional steps (0.1 + 0.2 …).
  const fixed = Number(snapped.toFixed(6));
  return clamp(fixed, min, Math.max(min, max));
}

export interface NumberStepperProps {
  value: number;
  onChange: (value: number) => void;
  min: number;
  max: number;
  step?: number;
  /** Enter pressed inside the stepper (e.g. submit the bid). */
  onSubmit?: (value: number) => void;
  /** Accessible label. */
  label: string;
  disabled?: boolean;
  size?: 'md' | 'lg';
  /** Unit suffix shown after the number (e.g. "puan"). */
  unit?: string;
  className?: string;
  autoFocus?: boolean;
}

function StepIcon({ plus, big }: { plus: boolean; big: boolean }) {
  const n = big ? 20 : 14;
  return (
    <svg aria-hidden width={n} height={n} viewBox="0 0 20 20">
      <rect x="1" y="7.5" width="18" height="5" fill="currentColor" />
      {plus && <rect x="7.5" y="1" width="5" height="18" fill="currentColor" />}
    </svg>
  );
}

/**
 * − [ 7 ] + stepper. Arrow Up/Down (and Right/Left on the buttons' group) change by `step`,
 * PageUp/PageDown by 5×step, Home/End jump to min/max, Enter calls `onSubmit`.
 * Typed input is clamped on blur/Enter.
 */
export function NumberStepper({
  value,
  onChange,
  min,
  max,
  step = 1,
  onSubmit,
  label,
  disabled,
  size = 'md',
  unit,
  className,
  autoFocus,
}: NumberStepperProps) {
  const id = useId();
  const [draft, setDraft] = useState<string | null>(null);
  const set = (v: number) => {
    const next = clampStep(v, min, max, step);
    if (next !== value) onChange(next);
    return next;
  };
  const commitDraft = () => {
    if (draft === null) return value;
    const parsed = Number(draft.replace(',', '.'));
    setDraft(null);
    return set(Number.isFinite(parsed) && draft.trim() !== '' ? parsed : value);
  };

  const onKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (disabled) return;
    const map: Record<string, number | undefined> = {
      ArrowUp: value + step,
      ArrowDown: value - step,
      PageUp: value + step * 5,
      PageDown: value - step * 5,
    };
    if (e.key in map) {
      e.preventDefault();
      setDraft(null);
      set(map[e.key]!);
    } else if (e.key === 'Home' && draft === null) {
      e.preventDefault();
      set(min);
    } else if (e.key === 'End' && draft === null) {
      e.preventDefault();
      set(max);
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const v = commitDraft();
      onSubmit?.(v);
    }
  };

  const big = size === 'lg';
  const atMin = value <= min;
  const atMax = value >= max;
  const btn = cn(
    'comic flex shrink-0 items-center justify-center border-ink bg-white leading-none select-none',
    'transition-colors hover:bg-yellow-soft active:bg-yellow',
    'aria-disabled:cursor-not-allowed aria-disabled:bg-[#eee8d8] aria-disabled:text-muted aria-disabled:hover:bg-[#eee8d8]',
    big ? 'w-12 text-[34px]' : 'w-10 text-[24px] sm:w-9',
  );

  return (
    <div
      className={cn(
        'inline-flex items-stretch border-ink bg-white',
        big ? 'h-16 border-4 shadow-ink' : 'h-11 border-3 shadow-ink-sm',
        disabled && 'opacity-60 grayscale',
        className,
      )}
    >
      <motion.button
        type="button"
        tabIndex={-1}
        aria-label="Azalt"
        aria-controls={id}
        aria-disabled={disabled || atMin || undefined}
        whileTap={disabled || atMin ? undefined : { scale: 0.85 }}
        onClick={() => !disabled && set(value - step)}
        className={cn(btn, big ? 'border-r-4' : 'border-r-3')}
      >
        <StepIcon plus={false} big={big} />
      </motion.button>
      <label htmlFor={id} className="sr-only">
        {label}
      </label>
      <div className="relative flex items-center">
        <input
          id={id}
          type="text"
          inputMode="decimal"
          role="spinbutton"
          aria-valuemin={min}
          aria-valuemax={max}
          aria-valuenow={value}
          aria-valuetext={unit ? `${value} ${unit}` : undefined}
          disabled={disabled}
          autoFocus={autoFocus}
          value={draft ?? String(value)}
          onChange={(e) => setDraft(e.target.value)}
          onBlur={commitDraft}
          onKeyDown={onKeyDown}
          onFocus={(e) => e.currentTarget.select()}
          className={cn(
            'comic h-full bg-transparent text-center text-ink outline-none',
            'focus-visible:bg-yellow-soft',
            big ? 'w-20 text-[40px]' : 'w-14 text-[26px]',
            unit && 'pr-1',
          )}
        />
        {unit && (
          <span
            className={cn('pr-2 font-extrabold text-ink-soft', big ? 'text-[14px]' : 'text-[12px]')}
          >
            {unit}
          </span>
        )}
      </div>
      <motion.button
        type="button"
        tabIndex={-1}
        aria-label="Artır"
        aria-controls={id}
        aria-disabled={disabled || atMax || undefined}
        whileTap={disabled || atMax ? undefined : { scale: 0.85 }}
        onClick={() => !disabled && set(value + step)}
        className={cn(btn, big ? 'border-l-4' : 'border-l-3')}
      >
        <StepIcon plus big={big} />
      </motion.button>
    </div>
  );
}
